import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import axios from 'axios';
import { ComponentPropsFromRoute } from '../../../routers/routers';
import { CatalogSearch } from '../search/catalog.search';
import { CatalogProductList } from '../product/list/catalog.product.list';
import { CatalogSearchResult } from '../search-result/catalog.search-result';
import { PaginatedRequest } from '../../../models/requests/paginated-request/paginated.request';
import { Pagination } from '../../../models/requests/paginated-result/paginated.result';
import { CatalogArticle, CatalogArticleMeta } from '../../../models/articles/catalog/search';
import {SearchParams, SearchType} from '../model/search';
import { useURLParams } from '../../../hooks/url-params.hook';
import { articlesService } from '../../../services/articles/articles.service';
import {
    serializeValue,
    urlParamsToInitialValues,
    valuesToPaginatedRequest,
} from './catalog.functions';
import i18n from 'i18next';
import {CatalogOEMSearchResult} from "../oem-search-result/catalog.oem-search-result";

export const Catalog: React.FC<ComponentPropsFromRoute> = () => {
    const history = useHistory();
    const { urlParams, setURLParams } = useURLParams();

    const [request, setRequest] = useState<PaginatedRequest | null>(null);
    const [items, setItems] = useState<CatalogArticle[]>([]);
    const [strictItems, setStrictItems] = useState<CatalogArticle[]>([]);
    const [oemItems, setOEMItems] = useState<any[]>([]);
    const [pagination, setPagination] = useState<Pagination<CatalogArticleMeta>>();
    const [loading, setLoading] = useState(false);
    const [shownLongLoadingMessage, setShownLongLoadingMessage] = useState(false);
    const [lastError, setLastError] = useState('');
    const [viewType, setViewType] = useState<'result' | 'oem-result'>('result');
    const [view, setView] = useState<'table' | 'cards'>('cards');
    const [lastFile, setLastFile] = useState<File>();

    const update = useCallback(async (newReq?: PaginatedRequest) => {

        const req = newReq ?? request;

        if (req?.filters['type'] === SearchType.OEM_LIST) {
            setViewType('oem-result')
            setRequest(null);
            setItems([]);
            setPagination(undefined);
            return;
        } else {
            setViewType('result')
        }

        if (!req || (newReq && request?.isEqual(newReq))) return;
        if (newReq) setRequest(newReq);

        setLoading(true);
        setOEMItems([]);

        const timeout = setTimeout(() => {
            setShownLongLoadingMessage(true);
        }, 5000);

        try {
            setItems([]);
            setPagination(undefined);

            const { items: fetched, meta } = await articlesService.search(req);
            setItems(fetched);
            setPagination(meta);
            setLastError('');

            if (req.filters.searchString && req.page === 1) {
                try {
                    const strict = await articlesService.strictSearch(req.filters.searchString);
                    setStrictItems(strict);
                } catch {
                    setStrictItems([]);
                }
            } else {
                setStrictItems([]);
            }
        } catch (e: unknown) {
            setLastError(
                axios.isAxiosError(e)
                    ? e.response?.data?.error || 'Server error'
                    : 'Unknown error'
            );
            setItems([]);
            setPagination(undefined);
            setStrictItems([]);
        } finally {
            clearTimeout(timeout);
            setLoading(false);
            setShownLongLoadingMessage(false);
        }
    }, [request]);

    useEffect(() => {
        const req = valuesToPaginatedRequest(urlParams);
        if (req) update(req);
    }, [urlParams, update]);

    useEffect(() => {
        const onLangChange = () => update();
        i18n.on('languageChanged', onLangChange);
        return () => i18n.off('languageChanged', onLangChange);
    }, [update]);

    const onPageChange = (page: number, limit: number) => {
        setURLParams(prev => ({
            ...prev,
            page: prev.limit !== String(limit) ? 1 : page,
            limit,
        }));
    };

    const onSearchChange = (params: SearchParams) => {

        if (params.searchString) {
            const [t, i, s, v] = params.searchString;
            if (t === "f") {
                history.push('/catalog/article/' + s)
                return
            }
        }

        const { searchString, ...rest } = params;
        setURLParams(prev => ({
            ...rest,
            ...(searchString ? { searchString: serializeValue(searchString) } : {}),
            page: 1,
            limit: prev.limit || 20,
        }));
    };

    return (
        <>
            <CatalogSearch
                onChange={onSearchChange}
                onOEMListSearch={async f => {
                    try {
                        setLoading(true);
                        setLastFile(f);
                        setOEMItems(await articlesService.searchByOEMList(f));
                    } finally {
                        setLoading(false);
                    }
                }}
                defaultValues={urlParamsToInitialValues(urlParams)}
                vehicle={pagination?.extra?.vehicle}
            />
            <div className="catalog_content_block">
                {viewType === 'oem-result' && lastFile ? (
                    <CatalogOEMSearchResult loading={loading} items={oemItems} file={lastFile} />
                ) : (
                    <>
                        {(items.length || strictItems.length) > 0 && (
                            <CatalogProductList
                                loading={loading}
                                onViewChange={setView}
                                pagination={pagination}
                                request={request}
                            />
                        )}
                        <CatalogSearchResult
                            loading={loading}
                            shownLongLoadingMessage={shownLongLoadingMessage}
                            view={view}
                            items={items}
                            strictItems={strictItems}
                            lastError={lastError}
                            pagination={pagination}
                            onPageChange={onPageChange}
                        />
                    </>
                )}
            </div>
        </>
    );
};
